/* ════════════════════════════════════════════════════════════════════
   src/auth/notifRoute.js — role-aware notification routing (QA NOT-001)

   A notification's `actionRoute` is written for its intended recipient, but
   the feed is shared: a Super Admin sees the whole network's notices. Following
   another role-app's route (e.g. a check-in's '/doctor/calendar', or an
   incident's '/desk/incidents') bounces the viewer through '/' to their own
   role home — which dumped the Super Admin on /clinics with no calendar/incident
   context. This resolver returns a route the CURRENT viewer can actually open,
   or null when navigating would misroute.

   Used by the Notifications page, the Header bell, and the NotificationBell.
   ════════════════════════════════════════════════════════════════════ */

import { getCurrentUser } from '@api/client';
import { activeRole } from '@auth/roles';

/* Which role-app a route belongs to — mirrors the route blocks in App.jsx. */
function appOfRoute(route) {
  const p = (route || '').split('?')[0];
  if (p === '/doctor' || p.startsWith('/doctor/')) return 'doctor';
  if (p === '/desk'   || p.startsWith('/desk/'))   return 'desk';
  if (p === '/my'     || p.startsWith('/my/'))     return 'patient';
  if (p === '/clinic' || p.startsWith('/clinic/')) return 'clinic';   // note: NOT /clinics
  return 'admin';   // the Super Admin app owns everything else (/clinics, /kyc, …)
}

const ROLE_APP = {
  superadmin:  'admin',
  clinicadmin: 'clinic',
  doctor:      'doctor',
  patient:     'patient',
  reception:   'desk',
  accountant:  'desk',
  masseur:     'desk',
  pharmacist:  'desk',
  rmo:         'desk',
};

/**
 * The route to navigate to when a notification is opened, for the current
 * viewer's role — or null if it shouldn't navigate (would misroute).
 *
 *   - route belongs to the viewer's own app  → follow it as-is
 *   - Super Admin + a foreign route + clinicId → that clinic's panel
 *   - otherwise                               → null (stay put, don't bounce)
 */
export function notifTargetRoute(n) {
  const route = n?.actionRoute;
  if (!route) return null;

  const myApp = ROLE_APP[activeRole(getCurrentUser())] || 'admin';
  if (appOfRoute(route) === myApp) return route;
  if (myApp === 'admin' && n.clinicId) {
    // The Super Admin has no calendar/schedule of its own, so land on that
    // clinic's panel. For a calendar/appointment event deep-link to its Day
    // summary (today's appointments) — the panel's calendar view (QA NOT-001).
    const base = `/clinics/${encodeURIComponent(n.clinicId)}/panel`;
    return /calendar|schedule|appointment/i.test(route) ? `${base}?tab=daysummary` : base;
  }
  return null;
}
